import React, { useEffect, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { Form, FormSpy } from "react-final-form";

import { makeApiCall } from "@lib/api/helpers";
import {
  getCountiesRCAApi,
  getCitiesRCAApi,
  getCompaniesLeasingApi,
  getCompanyTypesApi,
  getCompanyCaenCodesApi,
} from "@lib/api";
import OwnerTypeSection from "@containers/RCA/RCAProprietar/OwnerTypeSection";
import LeasingSection from "@containers/RCA/RCAProprietar/LeasingSection";
import CompanyDataSection from "@containers/RCA/RCAProprietar/CompanyDataSection";
import OwnerDataSection from "@containers/RCA/RCAProprietar/OwnerDataSection";
import MainDriverSection from "@containers/RCA/RCAProprietar/MainDriverSection";
import GdprConsentSection from "@containers/RCA/RCAProprietar/GdprConsentSection";
import Heading from "@components/Heading";
import Button from "@components/Button";
import {
  buildOwnerSectionPayload,
  getYearFromDateString,
} from "@containers/RCA/rcaHelpers";
import { addAlert } from "@store/app/actions";

const RCAProprietar = ({
  onApiDataUpdate,
  apiData,
  onStepChange,
  onFormDataUpdate,
  onLoadingStateChange,
  formData,
  isFetching,
  addAlert,
  onErrorsUpdate,
}) => {
  const {
    counties,
    leasingCompanies,
    cities,
    companyTypes,
    caenCodes,
    selectedVehicle,
    savedVehicles,
  } = apiData;

  const hasCompanyData = companyTypes?.length > 0 && caenCodes?.length > 0;

  useEffect(() => {
    if (!counties) {
      getFormData({ type: "countiesData" });
    }
  }, []);

  const fetchCounties = useCallback(async () => {
    if (counties?.length) {
      return;
    }

    const response = await makeApiCall({
      apiCall: getCountiesRCAApi,
      returnResponse: true,
      formatResponse: (response) => {
        const formatedCounties = response.data.data.items
          .map((county) =>
            county.name === "Bucuresti"
              ? {
                  ...county,
                  name: `Bucuresti ${county.code.replace("B", "S")}`,
                }
              : county
          )
          .sort((a, b) => a.name.localeCompare(b.name));

        return formatedCounties;
      },
      preFail: () => {
        addAlert({
          message: "Eroare la incarcarea judetelor.",
          variant: "error",
        });

        onErrorsUpdate({
          message: "Eroare la incarcarea judetelor.",
          variant: "error",
        });
      },
    });

    if (response?.length) {
      onApiDataUpdate({ counties: response });
    }
  }, []);

  const fetchLeasingCompanies = useCallback(async () => {
    if (leasingCompanies?.length) {
      return;
    }

    const response = await makeApiCall({
      apiCall: getCompaniesLeasingApi,
      returnResponse: true,
      formatResponse: (response) => response.data.data,
      preFail: () => {
        addAlert({
          message: "Eroare la incarcarea companiilor de leasing.",
          variant: "error",
        });

        onErrorsUpdate({
          message: "Eroare la incarcarea companiilor de leasing.",
          variant: "error",
        });
      },
    });

    if (response.items?.length) {
      onApiDataUpdate({ leasingCompanies: response.items });
    }
  }, []);

  const fetchCitiesInRegion = async (countyId) => {
    if (cities?.[countyId]?.length) {
      return;
    }

    const response = await makeApiCall({
      apiCall: getCitiesRCAApi,
      apiArgs: [countyId],
      formatResponse: (response) => response.data.data,
      preFail: () => {
        addAlert({
          message: "Eroare la incarcarea localitatilor.",
          variant: "error",
        });

        onErrorsUpdate({
          message: "Eroare la incarcarea localitatilor.",
          variant: "error",
        });
      },
    });

    if (response?.items?.length) {
      onApiDataUpdate({
        cities: {
          ...cities,
          [countyId]: response.items,
        },
      });
    }
  };

  const fetchCompanyTypes = useCallback(async () => {
    if (companyTypes?.length) {
      return;
    }

    const response = await makeApiCall({
      apiCall: getCompanyTypesApi,
      returnResponse: true,
      formatResponse: (response) => response.data.data,
      preFail: () => {
        addAlert({
          message: "Eroare la incarcarea tipurilor de companii.",
          variant: "error",
        });

        onErrorsUpdate({
          message: "Eroare la incarcarea tipurilor de companii.",
          variant: "error",
        });
      },
    });

    if (response?.items?.length) {
      onApiDataUpdate({ companyTypes: response.items });
    }
  }, []);

  const fetchCaenCodes = useCallback(async () => {
    if (caenCodes?.length) {
      return;
    }

    const response = await makeApiCall({
      apiCall: getCompanyCaenCodesApi,
      returnResponse: true,
      formatResponse: (response) => response.data.data,
      preFail: () => {
        addAlert({
          message: "Eroare la incarcarea codurilor CAEN.",
          variant: "error",
        });

        onErrorsUpdate({
          message: "Eroare la incarcarea codurilor CAEN.",
          variant: "error",
        });
      },
    });

    if (response?.items?.length) {
      onApiDataUpdate({ caenCodes: response.items });
    }
  }, []);

  const getFormData = async ({ type, payload }) => {
    onLoadingStateChange(true);

    switch (type) {
      case "companyData":
        await Promise.all([fetchCompanyTypes(), fetchCaenCodes()]);
        break;
      case "leasingData":
        await fetchLeasingCompanies();
        break;
      case "citiesInRegionData":
        await fetchCitiesInRegion(payload);
        break;
      case "countiesData":
        await fetchCounties();
        break;

      default:
        break;
    }

    onLoadingStateChange(false);
  };

  const handleFormValuesChange = ({ values, form }) => {
    if (JSON.stringify(values) !== JSON.stringify(formData.proprietarForm)) {
      onFormDataUpdate({ proprietarForm: values });
    }

    const isLeasing = values.isLeasing === "true";
    const isCompany = values.legalType === "PJ";
    const countyId = isCompany
      ? values.companyDataSection?.address?.countyId
      : values.userDataSection?.address?.countyId;

    // Fetch necessary data based on form values
    if (isCompany && !hasCompanyData && !isFetching) {
      getFormData({ type: "companyData" });
    }

    if (isLeasing && !leasingCompanies && !isFetching) {
      getFormData({ type: "leasingData" });
    }

    if (countyId && !cities?.[countyId]?.length && !isFetching) {
      getFormData({ type: "citiesInRegionData", payload: countyId });
    }

    if (!isLeasing && values.leasingCompanyCif) {
      form.change("leasingCompanyCif", "");
    }
  };

  const onSubmit = (formValues) => {
    const { isLeasing, leasingCompanyCif } = formValues;

    let leasingCompany;

    if (isLeasing === "true" && leasingCompanyCif) {
      leasingCompany = leasingCompanies.find(
        (company) => company.cif === leasingCompanyCif
      );
    }

    const ownerPayload = buildOwnerSectionPayload({
      formValues,
      leasingCompany,
    });

    const payload = {
      ...formData,
      vehicle: {
        ...formData.vehicle,
        ...ownerPayload,
        isLeasing: isLeasing === "true",
      },
    };

    onFormDataUpdate(payload);
    onStepChange(3);
  };

  const initialValues = useMemo(() => {
    if (formData.proprietarForm) {
      return formData.proprietarForm;
    }

    const vehicle = {
      ...savedVehicles?.[selectedVehicle],
    };

    // some address ids need to be removed from the payload
    if (
      vehicle.vehicleOwner?.address ||
      vehicle.vehicleUser?.address ||
      vehicle.vehicleDriver?.address
    ) {
      delete vehicle.vehicleOwner?.address?.id;
      delete vehicle.vehicleOwner?.addressId;
      delete vehicle.vehicleOwner?.id;

      delete vehicle.vehicleUser?.address?.id;
      delete vehicle.vehicleUser?.addressId;
      delete vehicle.vehicleUser?.id;

      delete vehicle.vehicleDriver?.address?.id;
      delete vehicle.vehicleDriver?.addressId;
      delete vehicle.vehicleDriver?.id;
    }

    const ownerLegalType = vehicle.isLeasing
      ? vehicle.vehicleUser?.legalType
      : vehicle.vehicleOwner?.legalType;

    return Object.keys(vehicle).length
      ? {
          // legalType block scoped to know which data to use in form payload
          legalType: ownerLegalType || "PF",
          userDataSection: vehicle.isLeasing
            ? {
                ...vehicle.vehicleUser,
                idExpirationDate: getYearFromDateString(
                  vehicle.vehicleUser?.idExpirationDate
                ),
                driverLicenceDate: getYearFromDateString(
                  vehicle.vehicleUser?.driverLicenceDate
                ),
              }
            : ownerLegalType === "PJ"
            ? {}
            : {
                ...vehicle.vehicleOwner,
                idExpirationDate: getYearFromDateString(
                  vehicle.vehicleOwner?.idExpirationDate
                ),
                driverLicenceDate: getYearFromDateString(
                  vehicle.vehicleOwner?.driverLicenceDate
                ),
              },

          companyDataSection: vehicle.isLeasing
            ? vehicle.vehicleUser
            : ownerLegalType === "PJ"
            ? { ...vehicle.vehicleOwner }
            : {},

          mainDriverSection: {
            mainDriver:
              vehicle.isLeasing || vehicle.vehicleOwner?.legalType === "PJ"
                ? "other"
                : "owner",
            mainDriverData: {
              ...vehicle.vehicleDriver,
              idExpirationDate: getYearFromDateString(
                vehicle.vehicleDriver?.idExpirationDate
              ),
              driverLicenceDate: getYearFromDateString(
                vehicle.vehicleDriver?.driverLicenceDate
              ),
            },
          },

          isLeasing: vehicle.isLeasing.toString(),
          ...(vehicle.isLeasing &&
            vehicle.vehicleOwner?.cif && {
              leasingCompanyCif: vehicle.vehicleOwner.cif,
            }),
        }
      : {
          legalType: "PF",
          companyDataSection: {},
          userDataSection: {},
          mainDriverSection: {
            mainDriver: "owner",
            mainDriverData: {},
          },
          isLeasing: "false",
        };
  }, [selectedVehicle, savedVehicles, formData.proprietarForm]);

  return (
    <Form
      key="RCAProprietarForm"
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ handleSubmit, form, values }) => {
        const isLeasing = values.isLeasing === "true";
        const isCompany = values.legalType === "PJ";

        return (
          <form onSubmit={handleSubmit}>
            <div className="space-y-[20px]">
              <Heading
                as="h4"
                title="Date despre proprietar"
                display="h2"
                sameSizeOnMobile
              />

              <FormSpy
                subscription={{ values: true }}
                onChange={({ values }) =>
                  handleFormValuesChange({ values, form })
                }
              />

              <OwnerTypeSection values={values} />

              <LeasingSection
                isLeasing={isLeasing}
                apiData={apiData}
                values={values}
              />

              {isCompany && (
                <div className="bg-white p-[16px]">
                  <CompanyDataSection form={form} apiData={apiData} />
                </div>
              )}

              {!isCompany && (
                <div className="bg-white p-[16px]">
                  <OwnerDataSection form={form} apiData={apiData} />
                </div>
              )}

              <MainDriverSection values={values} isCompany={isCompany} />

              <div className="bg-white pt-[20px] -mx-[15px] lg:-mx-[20px]">
                <div className="bg-[#F5FBF4] px-[15px] lg:px-[20px] py-[20px]">
                  <GdprConsentSection />
                </div>
              </div>
            </div>

            <div className="pt-[20px] text-center -mx-[15px] lg:-mx-[20px] -mb-[20px] bg-white">
              <Button
                variant="secondary"
                type="submit"
                title="Pasul urmator"
                classesProp="min-w-[300px] text-[20px] font-medium"
                hasMinWidth
              />
            </div>
          </form>
        );
      }}
    />
  );
};

export default connect(null, { addAlert })(RCAProprietar);
